// Reusable dark hero header — used across Company, Contact, Pricing, API, Blog pages
import React from "react";
import Section from "@/components/public/Section";
import { MiniNavBar } from "@/components/public/MiniNavBar";

// Purple accent color for dark hero headers
const HERO_ACCENT = "#a855f7"; // purple-500

// ─── Types ────────────────────────────────────────────────────────────────────

interface DarkHeroHeaderProps {
  /** Small uppercase label above title */
  eyebrow?: string;
  /** Pulsing badge (alternative to eyebrow, used on API pages) */
  badge?: string;
  /** Main heading */
  title: string;
  /** Optional tagline below title (teal colored, used on API pages) */
  tagline?: string;
  /** Supporting description text */
  description?: string;
  /** Additional content below description (e.g., billing toggle, buttons) */
  children?: React.ReactNode;
  /** Additional top padding for MiniNavBar area (default: "pt-6") */
  navPadding?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DarkHeroHeader({
  eyebrow,
  badge,
  title,
  tagline,
  description,
  children,
  navPadding = "pt-6",
}: DarkHeroHeaderProps) {
  return (
    <Section
      spacing="none"
      bgColor="var(--homepage-primary, #0d2434)"
      bottomPadding="pb-12 sm:pb-16 lg:pb-20"
    >
      {/* Subtle grid texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* MiniNavBar — light variant for dark bg */}
      <div className={`relative z-10 w-full overflow-visible sm:pl-0 md:pl-6 xl:pl-6 ${navPadding} mb-10`}>
        <MiniNavBar variant="light" />
      </div>

      {/* Hero content */}
      <div className="relative z-10 w-full overflow-visible sm:pl-0 md:pl-6 xl:pl-6">
        {/* Pulsing badge (API style) */}
        {badge && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 mb-8">
            <span
              className="w-2 h-2 rounded-full animate-pulse flex-shrink-0"
              style={{ backgroundColor: HERO_ACCENT }}
            />
            <span className="text-xs font-semibold text-white/60 uppercase tracking-widest">
              {badge}
            </span>
          </div>
        )}

        {/* Eyebrow (standard style) */}
        {eyebrow && !badge && (
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ color: HERO_ACCENT }}
          >
            {eyebrow}
          </p>
        )}

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight mb-4 max-w-2xl">
          {title}
        </h1>

        {/* Tagline (purple, used on API pages) */}
        {tagline && (
          <p
            className="text-lg sm:text-xl font-semibold mb-4"
            style={{ color: HERO_ACCENT }}
          >
            {tagline}
          </p>
        )}

        {/* Description */}
        {description && (
          <p className={`text-base sm:text-lg text-white/60 max-w-xl leading-relaxed ${children ? "mb-8" : ""}`}>
            {description}
          </p>
        )}

        {/* Custom children (buttons, toggles, etc.) */}
        {children}
      </div>
    </Section>
  );
}

export default DarkHeroHeader;
